import { useSyncExternalStore } from "react";

/**
 * "Count my visits automatically" — the opt-in for passive dwell detection
 * (`useDwellDetection`). OFF by default: until the diner flips the switch in
 * Settings, only an explicit Dine In tap records a visit.
 *
 * Stored per device in localStorage (`resto_auto_visits`). Fail-safe: blocked
 * storage reads as OFF, so the passive path never runs on a guess.
 */

const KEY = "resto_auto_visits";
const listeners = new Set<() => void>();

export function readAutoVisits(): boolean {
  try {
    return window.localStorage.getItem(KEY) === "on";
  } catch {
    return false;
  }
}

export function setAutoVisits(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(KEY, "on");
    else window.localStorage.removeItem(KEY);
  } catch {
    // Private mode / storage full — the switch simply won't stick.
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab flipping the switch keeps this one in sync.
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY || e.key === null) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

/** Live value of the opt-in; re-renders when it changes (this tab or another). */
export function useAutoVisits(): boolean {
  return useSyncExternalStore(subscribe, readAutoVisits, () => false);
}
